import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, NBTextarea, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, PawPrint } from "lucide-react";

const SPECIES = ["dog", "cat", "bird", "fish", "rabbit", "reptile", "other"];

export default function Pets() {
  const [pets, setPets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", species: "dog", breed: "", dob: "", feeding_schedule: "", vet_info: "", notes: "", image_url: "" });

  const load = async () => {
    const { data } = await api.get("/api/pets");
    setPets(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/pets", form);
      toast.success("Pet added!");
      setOpen(false);
      setForm({ name: "", species: "dog", breed: "", dob: "", feeding_schedule: "", vet_info: "", notes: "", image_url: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => { await api.delete(`/api/pets/${id}`); load(); };

  return (
    <div data-testid="pets-page">
      <PageHeader
        title="Pets"
        subtitle="Care schedules and info for your furry family."
        color="#B8CFA9"
        action={<NBButton testId="add-pet-btn" color="#B8CFA9" onClick={() => setOpen(true)}><Plus strokeWidth={3} className="w-4 h-4 inline mr-1" />Pet</NBButton>}
      />

      {pets.length === 0 ? (
        <EmptyState title="No pets yet" subtitle="Add your pets to track care schedules." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {pets.map((p) => (
            <NBCard key={p.id} data-testid={`pet-${p.id}`}>
              {p.image_url ? (
                <div className="aspect-square -m-6 mb-4 border-b-2 border-black overflow-hidden">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="aspect-square -m-6 mb-4 border-b-2 border-black bg-[#B8CFA9] flex items-center justify-center">
                  <PawPrint strokeWidth={2.5} className="w-20 h-20" />
                </div>
              )}
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>{p.name}</h4>
                  <span className="nb-badge bg-[#E8B96B] mt-1 inline-block capitalize">{p.species}</span>
                  {p.breed && <span className="nb-badge bg-white ml-1">{p.breed}</span>}
                </div>
                <button onClick={() => remove(p.id)} className="nb-btn bg-[#E9B5A0] p-1.5">
                  <Trash2 strokeWidth={2.5} className="w-4 h-4" />
                </button>
              </div>
              {p.dob && <p className="text-xs font-bold mt-2">🎂 {p.dob}</p>}
              {p.feeding_schedule && (
                <div className="mt-3">
                  <p className="text-xs font-black uppercase tracking-wider">Feeding</p>
                  <p className="text-sm whitespace-pre-wrap">{p.feeding_schedule}</p>
                </div>
              )}
              {p.vet_info && (
                <div className="mt-3">
                  <p className="text-xs font-black uppercase tracking-wider">Vet</p>
                  <p className="text-sm whitespace-pre-wrap">{p.vet_info}</p>
                </div>
              )}
              {p.notes && <p className="text-sm mt-3 italic">"{p.notes}"</p>}
            </NBCard>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Pet</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <NBInput testId="pet-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <NBSelect testId="pet-species" value={form.species} onChange={(e) => setForm({ ...form, species: e.target.value })}>
                {SPECIES.map((s) => <option key={s}>{s}</option>)}
              </NBSelect>
              <NBInput testId="pet-breed" placeholder="Breed" value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} />
              <NBInput testId="pet-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              <NBInput testId="pet-image" placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              <NBTextarea testId="pet-feeding" placeholder="Feeding schedule" value={form.feeding_schedule} onChange={(e) => setForm({ ...form, feeding_schedule: e.target.value })} />
              <NBTextarea testId="pet-vet" placeholder="Vet info" value={form.vet_info} onChange={(e) => setForm({ ...form, vet_info: e.target.value })} />
              <NBTextarea testId="pet-notes" placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <NBButton testId="submit-pet" type="submit" color="#B8CFA9" className="w-full">Save Pet</NBButton>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
